import {pushError} from '@js/actions/errorManagement';

import I18n from '@js/modules/translations';

import {FetchTimeout, ElevationRideTypes, HillyPlanningSpeedFactor, PaceProfileFactors} from '@js/components/modules/constants';

import Notification from '@js/components/layouts/notification';

import MapInterface from '@js/modules/maps/mapInterface';
import Polyline from '@js/modules/maps/polyline';
import MapRequest from '@js/modules/maps/mapRequest';
import {ROUND_TRIP_CANDIDATES} from '@js/modules/maps/mapConstants';

import AnalyticsService from '@js/modules/analyticsService';
import WebPerf from '@js/modules/webPerf';
import StorageService from '@js/modules/storage';


// Beyond this average gap between the drawn trace and the matched road, the road is considered
// "too far": we keep the freehand trace as-is instead of snapping it (Footpath-style behaviour).
const FREEHAND_MATCH_MAX_DEVIATION_METERS = 40;

// Cap the number of waypoints sent to the directions API when a failed /match is approximated by
// routing through sampled points of the trace.
const FREEHAND_MAX_SAMPLED_WAYPOINTS = 25;

// GraphHopper /match smoothing: treat drawn points as imprecise so the matcher doesn't overfit.
const FREEHAND_MATCH_GPS_ACCURACY_METERS = 50;

// Drop points closer than this to the previous kept point before matching: dense finger-drawn
// clusters are the main cause of spurious out-and-back detours in the matched path.
const FREEHAND_MATCH_MIN_POINT_SPACING_METERS = 20;

// Fallback activity speeds (km/h) used to convert a round-trip *duration* target into a distance,
// since GraphHopper's round_trip only accepts a target distance. Kept in sync with the server-provided
// window.averageRideTypeSpeed (the canonical source used everywhere else, incl. the displayed route
// duration and multi-day) so a "1h" loop matches the duration the rest of the app computes — this is
// only the fallback when that global is missing. The rendered loop's real duration stays approximate
// (terrain/elevation are ignored here). Keyed by ride type (mapStore.currentRideType).
const ROUND_TRIP_SPEED_KMH = {
    running: 9,
    hiking: 2,
    bike: 16,
    walk: 4.5
};
const ROUND_TRIP_DEFAULT_SPEED_KMH = 4.5;

// Storage key for the round-trip usage quota (map_max_round_trip feature flag).
const ROUND_TRIP_USAGE_STORAGE_KEY = 'map_max_round_trip_count';

const _routingOutcome = (data) => {
    if (!data) {
        return 'exception';
    }

    if (data.abort) {
        return data.abortReason === 'TimeoutError' ? 'timeout' : null;
    }

    // Over HTTP/2 a consumed error body leaves `errors` empty: the status still says it failed
    if (!data.errors && !(data.status >= 400)) {
        return 'ok';
    }

    if (data.errors === 'offline') {
        // api.js answers 'offline' for EVERY rejected fetch, and the routing host is cross-origin:
        // an nginx 502 page served without Access-Control-Allow-Origin is rejected by the browser
        // as "Failed to fetch", never as a 502. Only the browser saying it has no network makes
        // this the client's own problem — otherwise the service is simply unreachable, which is an
        // outage and has to count as one, or the rate is blind to the very event it measures.
        return (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : 'unreachable';
    }

    if (data.status >= 500) {
        return 'server_error';
    }

    if (data.status >= 400) {
        return 'no_route';
    }

    // An error with no HTTP status never reached the service (DNS, CORS, parse failure).
    return 'exception';
};

// Our routing refused the segment (4xx), is down (5xx), unreachable or too slow: Mapbox may still
// route it. Not a request the next one cancelled, nor a browser without network.
const _ourRoutingFailed = (data) => {
    if (!data) {
        return true;
    }

    if (data.abort) {
        return data.abortReason === 'TimeoutError';
    }

    if (data.errors === 'offline') {
        return !(typeof navigator !== 'undefined' && navigator.onLine === false);
    }

    return !!data.errors || data.status >= 400;
};

// The text api.js gives a failed request, or its HTTP status when HTTP/2 left that text empty
const _routingError = (data) => data?.errors || (data?.status ? `HTTP ${data.status}` : null);

const ComputeRoadMapModule = {
    // _computeRoadModel: {},

    getSnapToRoadsRoute(startPointLatLng, endPointLatLng, wayPointLatLng, callback, {
        limitDistance = false,
        distance = 0,
        routeType = undefined,
        // Live preview while a start/finish marker is dragged: one request per mousemove, so no
        // billed Mapbox retry and no toast. The drag end computes the final segment with both.
        preview = false
    } = {}) {
        if (!startPointLatLng || !endPointLatLng) {
            return;
        }

        if (limitDistance) {
            const distanceBetweenPoints = this.distHaversine(startPointLatLng, endPointLatLng, MapInterface.COORDINATES_PRECISION);
            const totalDistance = distance * 1000 + distanceBetweenPoints;
            if (totalDistance >= this._model.mapMaxDistance) {
                this.leaveFullscreen();

                // if (this.alertPopup) {
                // const hasOnlyPoint = this._mapModel.linePoints.length === 1;
                //
                //     this.alertPopup.fire({
                //         title: I18n.t('js.maps.errors.distance.title'),
                //         text: I18n.t('js.maps.errors.distance.text', {distance: parseInt(mapMaxDistance) / 1000}),
                //         confirmButtonText: I18n.t('js.maps.errors.distance.remove_last_point'),
                //         icon: 'warning',
                //         showDenyButton: hasOnlyPoint,
                //         denyButtonText: I18n.t('js.maps.errors.distance.remove_first_point'),
                //     })
                //         .then(function (event) {
                //             if (event.isConfirmed) {
                //                 // Remove last point
                //                 this.undo();
                //             } else if (event.isDenied) {
                //                 this.clear();
                //             }
                //         }.bind(this));
                // }

                this.undo();

                this.leaveFullscreen();

                if (this._model.showProductModal) {
                    this._model.showProductModal('map_max_distance');
                }

                return;
            }
        }

        this._model.isComputingRoad = true;

        // Computation time is very low, so it's useless
        // this.toggleLoader(I18n.t('js.maps.toolbar.snap_to_road.computing'));

        let pointsArray;
        if (wayPointLatLng !== null && typeof wayPointLatLng !== 'undefined') {
            pointsArray = [startPointLatLng, wayPointLatLng, endPointLatLng];
        } else {
            pointsArray = [startPointLatLng, endPointLatLng];
        }

        this._fetchRoute(pointsArray, callback, {
            routeType: routeType || this._model.routingRouteType,
            routingService: MapInterface.routingService.MAP_PLANNER,
            fallback: !preview,
            silent: preview,
            preview
        });

        // Cancel if no results, unless Mapbox is still redrawing the segment
        setTimeout(() => {
            if (!this._model.isRescuingRoute) {
                this._model.isComputingRoad = false;
            }
            // this.toggleLoader();
        }, FetchTimeout);
    },

    // Driving route (time to the ride start): always Mapbox, our GraphHopper serves no car profile.
    getDrivingRoute(startPointLatLng, endPointLatLng, callback) {
        if (!startPointLatLng || !endPointLatLng) {
            return;
        }

        this._fetchRoute([startPointLatLng, endPointLatLng], callback, {
            routeType: 'car',
            routingService: MapInterface.routingService.MAPBOX,
            sample: false
        });
    },

    // Generate a round-trip (loop) from a single start point, using GraphHopper's
    // algorithm=round_trip. `type` is 'distance' (value in m) or 'duration' (value in s, converted
    // to a distance via ROUND_TRIP_SPEED_KMH[rideType]). Replaces the current line with the generated
    // loop, written into the planner's canonical model (_mapModel.linePoints) exactly like a snapped
    // route — NOT the display-only _drawLineModel — so it renders, counts toward the distance, and
    // saves. Round trips are GraphHopper-only (silent so no Mapbox-fallback snap toast leaks; we
    // surface our own error). Seeds seed..seed+ROUND_TRIP_CANDIDATES-1 are requested in parallel and
    // the loop closest to the target distance wins (one quota use). Pass the next seed for a new loop.
    generateRoundTrip(startPointLatLng, {type = 'distance', value = 0, routeType = undefined, rideType = undefined, seed = 0} = {}, callback = undefined) {
        if (!startPointLatLng) {
            Notification.error(I18n.t('js.maps.toolbar.round_trip.no_start'));
            return;
        }

        // Usage quota (mirrors the isochrone quota): count uses and gate behind the paywall.
        if (this._model.mapMaxRoundTrip) {
            const roundTripCount = StorageService.getItem(ROUND_TRIP_USAGE_STORAGE_KEY) || 0;

            if (roundTripCount >= this._model.mapMaxRoundTrip) {
                if (this._model.showProductModal) {
                    this._model.showProductModal('map_max_round_trip');
                }

                return;
            }

            StorageService.setItem(ROUND_TRIP_USAGE_STORAGE_KEY, roundTripCount + 1);
        }

        // Convert a duration target into a distance. The displayed duration (Computation.estimateDuration)
        // scales the base speed by 2.5 on flat ground, but a loop's real elevation is unknown until it
        // exists and the same origin yields very different climbs by direction. So for elevation
        // activities (hiking/bike) we assume hilly terrain and use a lower factor — loops lean a bit
        // short and rarely overshoot the requested duration. Pace activities (running/walk) use the base.
        // The stored speed is normalised by the user's pace profile (Users::ComputeAverageSpeedJob
        // divides by it, because every duration estimate re-multiplies by it), so it is applied here
        // too — without it a `sport` account asking for a 1 h loop got one 20 % too short.
        const paceFactor   = PaceProfileFactors[window.currentUserPaceProfile] ?? 1.0;
        const baseSpeedKmh = (window.averageRideTypeSpeed?.[rideType]
            || ROUND_TRIP_SPEED_KMH[rideType]
            || ROUND_TRIP_DEFAULT_SPEED_KMH) * paceFactor;
        const speedKmh = ElevationRideTypes.includes(rideType) ? baseSpeedKmh * HillyPlanningSpeedFactor : baseSpeedKmh;
        const distance = type === 'duration'
            ? Math.round(speedKmh * 1000 / 3600 * value)
            : value;

        if (!distance || distance <= 0) {
            Notification.error(I18n.t('js.maps.toolbar.round_trip.error'));
            return;
        }

        this._model.isComputingRoad = true;
        this.toggleLoader(I18n.t('js.maps.toolbar.round_trip.computing'));

        const candidates = [];
        let pending = ROUND_TRIP_CANDIDATES;

        // Rank by the length the planner will display (haversine), not GraphHopper's own figure.
        const _deviation = (points) => {
            let length = 0;
            for (let i = 1; i < points.length; i++) {
                length += this.distHaversine(points[i - 1], points[i]);
            }
            return Math.abs(length - distance);
        };

        const _onAllAnswered = () => {
            this.toggleLoader();

            // Keep the loop closest to the target.
            const best = candidates.reduce((kept, candidate) => (!kept || candidate.deviation < kept.deviation ? candidate : kept), null);
            const routePoints = best?.routePoints;
            const routeProperties = best?.routeProperties;

            if (Array.isArray(routePoints) && routePoints.length > 1) {
                // Snapshot the current line before the destructive replace so a loop generation
                // can be undone like any other edit (only on success, to avoid a no-op undo step).
                this._pushUndoSnapShot();

                // Replace the current line with the loop. First/last point (the shared origin)
                // are manual endpoints; the rest are computed — same convention as getSnapToRoadsRoute.
                this._mapModel.linePoints = [];
                for (let i = 0; i < routePoints.length; i++) {
                    this._mapModel.linePoints.push(new MapInterface.PointInfo(routePoints[i], null, {
                        computed: i !== 0 && i !== routePoints.length - 1,
                        surface: routePoints[i].surface
                    }));
                }
                // Freshly generated loop: reset the computed-points offset used by marker dragging,
                // otherwise a stale value from a previous route mis-indexes the start/finish drag.
                this._model.previousRoadComputedPoints = 0;
                this._redrawKeyPoints();
                this._mapEditedFuncs();

                if (this.hasModule('RIDE_STORE') && this.storeRide) {
                    this.storeRide(this._mapModel.linePoints);
                }
            } else {
                Notification.error(I18n.t('js.maps.toolbar.round_trip.error'));
            }

            if (typeof callback === 'function') {
                callback(routePoints, routeProperties);
            }
        };

        for (let attempt = 0; attempt < ROUND_TRIP_CANDIDATES; attempt++) {
            this._fetchRoute([startPointLatLng], (routePoints, routeProperties) => {
                if (Array.isArray(routePoints) && routePoints.length > 1) {
                    candidates.push({routePoints, routeProperties, deviation: _deviation(routePoints)});
                }

                pending -= 1;
                if (pending === 0) {
                    _onAllAnswered();
                }
            }, {
                routeType: routeType || this._model.routingRouteType,
                routingService: MapInterface.routingService.MAP_PLANNER,
                roundTrip: {distance, seed: seed + attempt},
                waymarked: false,
                silent: true
            });
        }

        setTimeout(() => {
            this._model.isComputingRoad = false;
        }, FetchTimeout);
    },

    /* Private Methods
    ******************** */
    _fetchRoute(pointsArray, callback, {
        routeType = undefined,
        routingService = undefined,
        elevation = false,
        // Freehand matching keeps the raw trace silently on failure; don't surface snap-to-road toasts.
        silent = false,
        // Round-trip generation ({ distance, seed }): GraphHopper builds a loop from one origin.
        roundTrip = null,
        // Retry on Mapbox when our routing fails (refused point, down, unreachable or too slow).
        fallback = true,
        // A marker-drag preview: one per mousemove, not a compute the user waits for (no timing).
        preview = false,
        // false: never the waymarked axis (loops, freehand traces), as the isochrone and the mobile app
        waymarked = true,
        // false: not a sample of the planner's routing (availability funnel, route_compute timing)
        sample = true,
        // Set on a Mapbox retry: the outcome of our own routing it stands in for
        rescuedFrom = null
    } = {}) {
        // A round trip is NOT a routing availability sample. It runs GraphHopper's flexible mode
        // (algorithm=round_trip&ch.disable=true), slow by design and the first thing to exceed
        // FetchTimeout on a long loop — counting that as an outage would report the engine as down
        // while snap-to-road is perfectly healthy. A failed loop is not a straight line either: the
        // user gets the round-trip error toast and no route at all.
        const startedAt = performance.now();
        const reportOutcome = (status) => {
            if (roundTrip || !sample) {
                return;
            }

            // Our failure stays an outage of ours, but a segment Mapbox drew is no straight line
            if (rescuedFrom) {
                AnalyticsService.trackRoutingOutcome(rescuedFrom, MapInterface.routingService.MAP_PLANNER, {rescued: status === 'ok'});
            }

            if (status) {
                AnalyticsService.trackRoutingOutcome(status, routingService);
                if (!preview) {
                    WebPerf.record('route_compute', performance.now() - startedAt, {outcome: status, service: routingService});
                }
            }
        };

        // A failure Mapbox may rescue toasts nothing: if the retry fails too, it tells the user
        const canFallBack = routingService !== MapInterface.routingService.MAPBOX && !roundTrip && fallback;

        return MapRequest.getDirectionsAPIUrl(pointsArray, routingService, {
            routeType,
            elevation,
            roundTrip,
            // Routing safety axes (hiking/walk/running, GraphHopper only — Mapbox degrades to the base
            // profile): waymarked = the map overlay toggle; preferPaths = avoid car roads.
            waymarkedOnly: waymarked && this._model.waymarkedTrailsEnabled,
            preferPaths: this._model.preferPathsEnabled
        }, {
            timeout: FetchTimeout,
            // An offline browser gets no Mapbox retry: its "no network" toast stays
            silentOffline: canFallBack && !(typeof navigator !== 'undefined' && navigator.onLine === false),
            silentErrors: canFallBack
        })
            .then((data) => {
                this._model.routingService = routingService;

                // Round trips are GraphHopper-only: don't fall back to Mapbox (a single-point
                // directions request can't build a loop anyway) — surface our own error instead.
                // The retry concerns this segment only: the next one asks our routing again.
                if (canFallBack && _ourRoutingFailed(data) && this._consumeMapboxQuota()) {
                    if (_routingError(data)) {
                        AnalyticsService.trackRoutingError(_routingError(data));
                    }

                    // Busy until Mapbox answers: a click meanwhile would start from the previous point
                    this._model.isComputingRoad = true;
                    this._model.isRescuingRoute = true;

                    return this._fetchRoute(pointsArray, callback, {
                        routeType,
                        routingService: MapInterface.routingService.MAPBOX,
                        elevation,
                        silent,
                        rescuedFrom: _routingOutcome(data)
                    })
                        .finally(() => {
                            this._model.isRescuingRoute = false;
                        });
                }

                if (!data || data.errors || data.status >= 400) {
                    if (_routingError(data)) {
                        AnalyticsService.trackRoutingError(_routingError(data));
                    }

                    reportOutcome(_routingOutcome(data));

                    if (!silent) {
                        Notification.error(I18n.t('js.maps.toolbar.snap_to_road.error'));
                    }

                    callback();

                    return;
                } else if (data.abort) {
                    // Only a timeout is an outage here: an abort caused by the next drag is not reported.
                    reportOutcome(_routingOutcome(data));

                    if (!silent) {
                        Notification.error(I18n.t('js.maps.toolbar.snap_to_road.network_error'));
                    }

                    callback();

                    return;
                }

                let encodedPoints;
                const routeProperties = {};
                if (routingService === MapInterface.routingService.MAPBOX) {
                    encodedPoints = data.routes && data.routes.length > 0 ? data.routes[0].geometry : null;
                    if (data.routes?.[0]) {
                        routeProperties.distance = data.routes[0].distance;
                        routeProperties.duration = data.routes[0].duration * 1000;
                    }
                } else if (data.paths) {
                    encodedPoints = data.paths && data.paths.length > 0 ? data.paths[0].points : null;
                    if (data.paths[0]) {
                        routeProperties.elevationGain = data.paths[0].ascend;
                        routeProperties.elevationLoss = data.paths[0].descend;
                        routeProperties.distance = data.paths[0].distance;
                        routeProperties.duration = data.paths[0].time;
                        routeProperties.surfaceDetails = data.paths[0].details?.surface;
                        routeProperties.roadClassDetails = data.paths[0].details?.road_class;
                    }
                }

                if (!encodedPoints) {
                    // The engine answered 200 with an empty polyline: there is no road, not an outage.
                    reportOutcome('no_route');

                    if (!silent) {
                        Notification.error(I18n.t('js.maps.toolbar.snap_to_road.error'));
                    }
                    callback();
                    return;
                }

                reportOutcome('ok');

                callback(this._buildRouteFromEncoded(encodedPoints, routeProperties, {elevation}), routeProperties);
            })
            .catch((error) => {
                pushError(error);
                reportOutcome('exception');

                if (!silent) {
                    Notification.error(I18n.t('js.maps.toolbar.snap_to_road.network_error'));
                }

                callback();
            })
            .finally(() => {
                setTimeout(() => {
                    this._model.isComputingRoad = false;
                    // this.toggleLoader();
                }, 100);
            });
    },

    // Decode a GraphHopper/Mapbox encoded polyline into a [{lat, lng, roadClass, surface}] route,
    // applying the road_class/surface details ranges when present.
    _buildRouteFromEncoded(encodedPoints, routeProperties = {}, {elevation = false} = {}) {
        const currentRoute = [];
        const decodedArray = Polyline.decode(encodedPoints, {elevation});
        for (let j = 0; j < decodedArray.length; j++) {
            currentRoute.push({
                lat: decodedArray[j][0],
                lng: decodedArray[j][1]
            });
        }

        if (routeProperties.roadClassDetails) {
            for (const [from, to, roadClass] of routeProperties.roadClassDetails) {
                for (let k = from; k < to && k < currentRoute.length; k++) {
                    currentRoute[k].roadClass = roadClass;
                }
            }
        }

        if (routeProperties.surfaceDetails) {
            for (const [from, to, surfaceType] of routeProperties.surfaceDetails) {
                for (let k = from; k < to && k < currentRoute.length; k++) {
                    if (surfaceType && surfaceType !== 'missing') {
                        currentRoute[k].surface = surfaceType;
                    } else {
                        currentRoute[k].surface = MapInterface.surfaceFromRoadClass(currentRoute[k].roadClass);
                    }
                }
            }
        }

        return currentRoute;
    },

    /* Freehand map-matching (Footpath-style)
    ******************** */

    // Match a freehand-drawn trace onto the nearest roads. `callback` is always invoked once with
    // an outcome object:
    //   { status: 'matched', route, properties } -> use the road-matched route
    //   { status: 'raw' }                        -> road too far / quota reached: keep the raw trace
    //   { status: 'aborted' }                    -> distance limit reached or error: discard the stroke
    getMatchedRoute(rawLatLngs, callback, {
        limitDistance = false,
        distance = 0,
        routeType = undefined
    } = {}) {
        if (!rawLatLngs || rawLatLngs.length < 2) {
            callback({status: 'aborted'});
            return;
        }

        if (limitDistance) {
            let drawnMeters = 0;
            for (let i = 1; i < rawLatLngs.length; i++) {
                drawnMeters += this.distHaversine(rawLatLngs[i - 1], rawLatLngs[i], MapInterface.COORDINATES_PRECISION);
            }

            const totalDistance = distance * 1000 + drawnMeters;
            if (totalDistance >= this._model.mapMaxDistance) {
                this.leaveFullscreen();

                if (this._model.showProductModal) {
                    this._model.showProductModal('map_max_distance');
                }

                callback({status: 'aborted'});

                return;
            }
        }

        this._model.isComputingRoad = true;

        // A failed match falls back to _matchViaDirections -> _fetchRoute, whose Mapbox retry
        // counts itself in the quota (_consumeMapboxQuota).
        this._fetchMatch(rawLatLngs, callback, {
            routeType: routeType || this._model.routingRouteType,
            routingService: MapInterface.routingService.MAP_PLANNER
        });

        setTimeout(() => {
            this._model.isComputingRoad = false;
        }, FetchTimeout);
    },

    _fetchMatch(rawLatLngs, callback, {
        routeType = undefined,
        routingService = undefined
    } = {}) {
        // Thin dense clusters before matching (keeps first/last); reduces out-and-back artifacts.
        const matchPoints = this._thinTrace(rawLatLngs, FREEHAND_MATCH_MIN_POINT_SPACING_METERS);

        MapRequest.matchTrace(matchPoints, {
            routeType,
            elevation: false,
            gpsAccuracy: FREEHAND_MATCH_GPS_ACCURACY_METERS,
            // Prefer-paths as in _fetchRoute, never the waymarked axis: /match is not bounded by max_visited_nodes
            waymarkedOnly: false,
            preferPaths: this._model.preferPathsEnabled
        }, {timeout: FetchTimeout})
            .then((data) => {
                // Timeout / aborted: don't fire a second full-timeout fallback request; keep the raw trace.
                // Terminal branch (no fallback follows), so it is the one that reports the outcome.
                if (data && data.abort) {
                    const abortOutcome = _routingOutcome(data);
                    if (abortOutcome) {
                        AnalyticsService.trackRoutingOutcome(abortOutcome, routingService);
                    }

                    callback({status: 'raw'});
                    return;
                }

                if (!data || data.errors || !data.paths || !data.paths.length || !data.paths[0].points) {
                    if (data?.errors) {
                        AnalyticsService.trackRoutingError(data.errors);
                    }

                    // NOT reported here: the fallback below calls _fetchRoute, which reports the
                    // outcome the user actually gets. Reporting both would count one failure twice.
                    this._matchViaDirections(rawLatLngs, callback, {routeType, routingService});

                    return;
                }

                AnalyticsService.trackRoutingOutcome('ok', routingService);

                const path = data.paths[0];
                const routeProperties = {
                    elevationGain: path.ascend,
                    elevationLoss: path.descend,
                    distance: path.distance,
                    duration: path.time,
                    surfaceDetails: path.details?.surface,
                    roadClassDetails: path.details?.road_class
                };

                const matchedRoute = this._buildRouteFromEncoded(path.points, routeProperties, {elevation: false});

                this._resolveMatchOutcome(matchedRoute, routeProperties, rawLatLngs, callback);
            })
            .catch((error) => {
                pushError(error);
                AnalyticsService.trackRoutingOutcome('exception', routingService);

                callback({status: 'aborted'});
            })
            .finally(() => {
                setTimeout(() => {
                    this._model.isComputingRoad = false;
                }, 100);
            });
    },

    // The Mapbox retry of a GraphHopper segment is a billed call: a limited user's quota covers it
    // like any other Mapbox computation. False when that quota is spent.
    _consumeMapboxQuota() {
        if (!this._model.hasMapLimitations || !this._model.mapMaxRouteComputationCount) {
            return true;
        }

        const mapMaxRouteComputationCount = (StorageService.getItem('map_max_route_computation_count') || 0) + 1;

        if (mapMaxRouteComputationCount > this._model.mapMaxRouteComputationCount) {
            AnalyticsService.trackRoutingOutcome('quota', MapInterface.routingService.MAPBOX);

            return false;
        }

        StorageService.setItem('map_max_route_computation_count', mapMaxRouteComputationCount);

        return true;
    },

    _matchViaDirections(rawLatLngs, callback, {
        routeType = undefined,
        routingService = undefined
    } = {}) {
        const waypoints = this._sampleTrace(rawLatLngs, FREEHAND_MAX_SAMPLED_WAYPOINTS);

        this._fetchRoute(waypoints, (route, properties) => {
            this._resolveMatchOutcome(route, properties, rawLatLngs, callback);
        }, {
            routeType,
            routingService,
            elevation: false,
            // Freehand keeps the raw trace silently on failure; suppress snap-to-road toasts.
            silent: true,
            waymarked: false
        });
    },

    _resolveMatchOutcome(route, routeProperties, rawLatLngs, callback) {
        if (!route || !route.length) {
            // No usable route: keep the freehand trace as-is.
            callback({status: 'raw'});
            return;
        }

        if (this._averageDeviation(rawLatLngs, route) > FREEHAND_MATCH_MAX_DEVIATION_METERS) {
            callback({status: 'raw'});
            return;
        }

        callback({status: 'matched', route, properties: routeProperties || {}});
    },

    // Drop points closer than `minMeters` to the previous kept point (always keeping first & last).
    // Evenly spaced points let the matcher prefer the straight road instead of inserting detours.
    _thinTrace(points, minMeters) {
        if (points.length <= 2) {
            return points;
        }

        const thinned = [points[0]];
        for (let i = 1; i < points.length - 1; i++) {
            if (this.distHaversine(thinned[thinned.length - 1], points[i], MapInterface.COORDINATES_PRECISION) >= minMeters) {
                thinned.push(points[i]);
            }
        }
        thinned.push(points[points.length - 1]);

        return thinned;
    },

    // Evenly sample a trace down to at most `maxPoints`, always keeping first and last points.
    _sampleTrace(points, maxPoints = FREEHAND_MAX_SAMPLED_WAYPOINTS) {
        if (points.length <= maxPoints) {
            return points;
        }

        const sampled = [];
        const step = (points.length - 1) / (maxPoints - 1);
        for (let i = 0; i < maxPoints; i++) {
            sampled.push(points[Math.round(i * step)]);
        }

        return sampled;
    },

    // Average, over every drawn point, of its distance (m) to the nearest matched-route SEGMENT.
    // Measuring to segments (not just vertices) avoids over-rejecting good matches on long straight
    // roads, where correct geometry has few, far-apart vertices.
    _averageDeviation(rawPoints, routePoints) {
        if (!rawPoints.length || routePoints.length < 2) {
            return Infinity;
        }

        let total = 0;
        for (const rawPoint of rawPoints) {
            let minDistance = Infinity;
            for (let i = 0; i < routePoints.length - 1; i++) {
                const distance = this._distanceToSegmentMeters(rawPoint, routePoints[i], routePoints[i + 1]);
                if (distance < minDistance) {
                    minDistance = distance;
                }
            }
            total += minDistance;
        }

        return total / rawPoints.length;
    },

    // Shortest distance (m) from `point` to the segment [segStart, segEnd], via a local
    // equirectangular projection to meters around `point` (accurate enough at the ~40 m scale).
    _distanceToSegmentMeters(point, segStart, segEnd) {
        const metersPerDegLat = (MapInterface.EARTH_RADIUS * 1000 * Math.PI) / 180;
        const metersPerDegLng = metersPerDegLat * Math.cos(point.lat * MapInterface.DEG_TO_RAD);

        const ax = (segStart.lng - point.lng) * metersPerDegLng;
        const ay = (segStart.lat - point.lat) * metersPerDegLat;
        const bx = (segEnd.lng - point.lng) * metersPerDegLng;
        const by = (segEnd.lat - point.lat) * metersPerDegLat;

        const dx = bx - ax;
        const dy = by - ay;
        const lengthSq = dx * dx + dy * dy;

        // point is at the origin (0, 0) in this local frame.
        if (lengthSq === 0) {
            return Math.hypot(ax, ay);
        }

        let t = -(ax * dx + ay * dy) / lengthSq;
        t = Math.max(0, Math.min(1, t));

        return Math.hypot(ax + t * dx, ay + t * dy);
    }
};

export default ComputeRoadMapModule;
